import { LoggerService } from '@logz-pkg/frontend-services';
import type { StateRule, UrlParts, UrlRule } from '@uirouter/react';
import { stateRedirections } from './stateRedirection';
import { appRouter } from 'services/router/router.service';
import { registerAppRouteStates } from 'services/router/register-app-route-states';

export const NEW_APP_ROOT = '/360/' as const;

type NewAppUrl = `/360/${string}`;

const isStateRule = (rule?: UrlRule): rule is StateRule => rule?.type === 'STATE';

/**
 * A repeated key keeps every value, so `array: true` params resolve the way a real transition
 * resolves them. Collapsing `?statuses=a&statuses=b` to the last value alone would hand the redirect
 * map a different array than the router builds, and it reads `statuses[0]`.
 */
const toSearch = (query: string): Record<string, string | string[]> => {
  const params = new URLSearchParams(query);

  return Object.fromEntries(
    [...new Set(params.keys())].map(key => {
      const values = params.getAll(key);

      return [key, values.length > 1 ? values : values[0]];
    }),
  );
};

/** The legacy app is hash-routed, so the whole deep link lives after the `#`. */
const toUrlParts = (hash: string): UrlParts | null => {
  const [path, query = ''] = (hash.startsWith('#') ? hash.slice(1) : hash).split('?');

  if (!path || path === '/') return null;

  return { path, search: toSearch(query) };
};

/**
 * Where this user belongs in the new application, given the legacy URL they arrived on.
 *
 * Every redirect that exists because the user may not stay in the old app resolves through here, so
 * that whichever one fires first produces the same destination and none of them can silently drop a
 * deep link. Falls back to the new app's root whenever the URL carries nothing worth preserving.
 *
 * Not for redirects that deliberately mean "go home" — those want `NEW_APP_ROOT` directly.
 */
export const resolveNewAppUrl = ({ hash }: Pick<Location, 'hash'>): NewAppUrl => {
  const urlParts = toUrlParts(hash);

  if (!urlParts) return NEW_APP_ROOT;

  try {
    registerAppRouteStates();

    const matched = appRouter.urlService.match(urlParts);

    if (!isStateRule(matched?.rule)) return NEW_APP_ROOT;

    return stateRedirections[matched.rule.state.name]?.(matched.match ?? {}) ?? NEW_APP_ROOT;
  } catch (error) {
    LoggerService.logError({ message: 'Failed to resolve a new application url', error });

    return NEW_APP_ROOT;
  }
};
